import validator from 'validator';

import type { TargetDomainEntry } from '@/lib/types/tenant';

/**
 * Resolves the public-facing base URL from a request, accounting for reverse proxies.
 *
 * Priority: x-original-host → x-forwarded-host → host header → request URL origin.
 */
export function getBaseUrl(request: Request): string {
  const originalHost = request.headers.get('x-original-host');
  const forwardedHost = request.headers.get('x-forwarded-host');
  const hostHeader = request.headers.get('host');
  const forwardedProto = request.headers.get('x-forwarded-proto') || 'https';

  const host = originalHost || forwardedHost || hostHeader;
  if (host) {
    return `${forwardedProto}://${host}`;
  }
  return new URL(request.url).origin;
}

export function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

export function stripWww(hostname: string): string {
  return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
}

export function hostnamesMatch({ a, b }: { a: string; b: string }): boolean {
  return stripWww(a) === stripWww(b);
}

export function isLocalhost(hostname: string): boolean {
  return hostname === 'localhost' || hostname.endsWith('.localhost');
}

export function matchesDomain({
  hostname,
  pattern,
}: {
  hostname: string;
  pattern: string;
}): boolean {
  if (pattern.startsWith('*.')) {
    const baseDomain = pattern.slice(2);
    return hostname === baseDomain || hostname.endsWith(`.${baseDomain}`);
  }
  return hostname === pattern || hostname.endsWith(`.${pattern}`);
}

export function getDomainPattern(entry: TargetDomainEntry): string {
  return typeof entry === 'string' ? entry : entry.pattern;
}

/**
 * Returns the first entry whose pattern matches `hostname`. Order matters —
 * callers should list more specific patterns before wildcards.
 */
export function findMatchingDomainEntry({
  hostname,
  entries,
}: {
  hostname: string;
  entries: readonly TargetDomainEntry[];
}): TargetDomainEntry | null {
  for (const entry of entries) {
    if (matchesDomain({ hostname, pattern: getDomainPattern(entry) })) return entry;
  }
  return null;
}

export function urlMatchesDomains({
  href,
  domains,
}: {
  href: string;
  domains: readonly TargetDomainEntry[];
}): boolean {
  const hostname = getHostname(href);
  if (!hostname) return false;
  return findMatchingDomainEntry({ hostname, entries: domains }) !== null;
}

export function isValidHttpUrl(href: string): boolean {
  return validator.isURL(href, { require_protocol: true, protocols: ['http', 'https'] });
}

export function matchesUrlPattern({ url, pattern }: { url: string; pattern: string }): boolean {
  const regexPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(regexPattern).test(url);
}

export function matchesAnyPattern({
  url,
  patterns,
}: {
  url: string;
  patterns: string | string[];
}): boolean {
  const patternList = Array.isArray(patterns) ? patterns : [patterns];
  return patternList.some(pattern => matchesUrlPattern({ url, pattern }));
}

export function applyQueryParams({
  url,
  params,
}: {
  url: string;
  params: Record<string, string>;
}): string {
  try {
    const urlObj = new URL(url);
    Object.entries(params).forEach(([key, value]) => {
      urlObj.searchParams.set(key, value);
    });
    return urlObj.toString();
  } catch {
    return url;
  }
}

export function interpolateTemplate({
  template,
  tokens,
}: {
  template: string;
  tokens: Record<string, string>;
}): string {
  return template.replace(/\{([\w-]+)\}/g, (match, key) => tokens[key] ?? match);
}
